import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DashboardLayout } from '@/components/DashboardLayout';
import { getInvoices, createInvoice, getDashboardStats, sendInvoiceToBM } from '@/services/api';
import { toast } from 'sonner';
import { 
  FileText, 
  Plus, 
  RefreshCw, 
  AlertCircle,
  Loader2,
  ExternalLink,
  CheckCircle,
  CheckCircle2,
  XCircle,
  Clock,
  Send,
  Ban
} from 'lucide-react';
import { motion } from 'framer-motion';

const getStatusBadge = (status) => {
  switch (status?.toLowerCase()) {
    case 'created': return { className: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 };
    case 'sent': return { className: 'bg-green-100 text-green-800', icon: Send };
    case 'draft': return { className: 'bg-blue-100 text-blue-800', icon: Clock };
    case 'error': return { className: 'bg-red-100 text-red-800', icon: XCircle };
    default: return { className: 'bg-slate-100 text-slate-800', icon: Clock };
  }
};

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pennylaneConnected, setPennylaneConnected] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sendingBM, setSendingBM] = useState(null);
  const [newInvoice, setNewInvoice] = useState({
    order_id: '',
    customer_name: '',
    customer_email: '',
    customer_address: '',
    items: [{ label: '', quantity: 1, price: 0 }],
    total_amount: 0,
    currency: 'EUR'
  });

  useEffect(() => {
    checkConnection();
    fetchInvoices();
  }, []);

  const checkConnection = async () => {
    try {
      const stats = await getDashboardStats();
      setPennylaneConnected(stats.pennylane_connected);
    } catch (error) {
      setPennylaneConnected(false);
    } finally {
      setCheckingConnection(false);
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const data = await getInvoices();
      setInvoices(data);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    const total = newInvoice.items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
    setNewInvoice(prev => ({ ...prev, total_amount: total }));
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...newInvoice.items];
    updatedItems[index] = { ...updatedItems[index], [field]: field === 'label' ? value : parseFloat(value) || 0 };
    setNewInvoice(prev => ({ ...prev, items: updatedItems }));
    setTimeout(calculateTotal, 0);
  };

  const addItem = () => {
    setNewInvoice(prev => ({
      ...prev,
      items: [...prev.items, { label: '', quantity: 1, price: 0 }]
    }));
  };

  const removeItem = (index) => {
    if (newInvoice.items.length > 1) {
      const updatedItems = newInvoice.items.filter((_, i) => i !== index);
      setNewInvoice(prev => ({ ...prev, items: updatedItems }));
      setTimeout(calculateTotal, 0);
    }
  };

  const handleCreateInvoice = async () => {
    if (!newInvoice.order_id || !newInvoice.customer_name) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setCreating(true);
    try {
      await createInvoice(newInvoice);
      toast.success('Facture créée avec succès');
      setDialogOpen(false);
      setNewInvoice({
        order_id: '',
        customer_name: '',
        customer_email: '',
        customer_address: '',
        items: [{ label: '', quantity: 1, price: 0 }],
        total_amount: 0,
        currency: 'EUR'
      });
      fetchInvoices();
    } catch (error) {
      console.error('Failed to create invoice:', error);
      const message = error.response?.data?.detail || 'Erreur lors de la création de la facture';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  if (checkingConnection) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="invoices-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Factures
            </h1>
            <p className="text-slate-600 mt-1">
              Générez et gérez vos factures Pennylane
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={fetchInvoices} 
              disabled={loading}
              variant="outline"
              data-testid="refresh-invoices-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-emerald-500 hover:bg-emerald-600"
                  data-testid="new-invoice-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle facture
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Créer une facture</DialogTitle>
                  <DialogDescription>
                    Remplissez les informations pour générer une facture Pennylane
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>ID Commande *</Label>
                      <Input
                        value={newInvoice.order_id}
                        onChange={(e) => setNewInvoice(prev => ({ ...prev, order_id: e.target.value }))}
                        placeholder="BM-123456"
                        data-testid="invoice-order-id-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nom du client *</Label>
                      <Input
                        value={newInvoice.customer_name}
                        onChange={(e) => setNewInvoice(prev => ({ ...prev, customer_name: e.target.value }))}
                        placeholder="Jean Dupont"
                        data-testid="invoice-customer-name-input"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newInvoice.customer_email}
                        onChange={(e) => setNewInvoice(prev => ({ ...prev, customer_email: e.target.value }))}
                        placeholder="client@email.com"
                        data-testid="invoice-customer-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Adresse</Label>
                      <Input
                        value={newInvoice.customer_address}
                        onChange={(e) => setNewInvoice(prev => ({ ...prev, customer_address: e.target.value }))}
                        placeholder="123 Rue Example, Paris"
                        data-testid="invoice-customer-address-input"
                      />
                    </div>
                  </div>

                  {/* Items */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Articles</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        Ajouter
                      </Button>
                    </div>
                    {newInvoice.items.map((item, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-6">
                          <Input
                            placeholder="Description"
                            value={item.label}
                            onChange={(e) => updateItem(index, 'label', e.target.value)}
                            data-testid={`item-label-${index}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            placeholder="Qté"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                            data-testid={`item-quantity-${index}`}
                          />
                        </div>
                        <div className="col-span-3">
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Prix €"
                            value={item.price}
                            onChange={(e) => updateItem(index, 'price', e.target.value)}
                            data-testid={`item-price-${index}`}
                          />
                        </div>
                        <div className="col-span-1">
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="icon"
                            onClick={() => removeItem(index)}
                            disabled={newInvoice.items.length === 1}
                          >
                            <XCircle className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <span className="font-medium">Total</span>
                    <span className="text-xl font-bold text-emerald-600">
                      {newInvoice.total_amount.toFixed(2)} €
                    </span>
                  </div>

                  {!pennylaneConnected && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-amber-800 font-medium">Pennylane non connecté</p>
                          <p className="text-xs text-amber-700 mt-1">
                            La facture sera créée localement mais ne sera pas envoyée à Pennylane.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button 
                    className="w-full bg-emerald-500 hover:bg-emerald-600"
                    onClick={handleCreateInvoice}
                    disabled={creating}
                    data-testid="create-invoice-submit-btn"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Création...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        Créer la facture
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Connection Status */}
        {!pennylaneConnected && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-amber-900">Pennylane non connecté</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    Connectez Pennylane dans les paramètres pour envoyer automatiquement vos factures.
                  </p>
                  <Button 
                    variant="outline" 
                    className="mt-4 border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={() => window.location.href = '/settings'}
                    data-testid="go-to-settings-btn"
                  >
                    Configurer Pennylane
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Invoices List */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Historique des factures
            </CardTitle>
            <CardDescription>
              {invoices.length} facture(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucune facture créée</p>
                <p className="text-sm text-slate-400 mt-1">
                  Cliquez sur "Nouvelle facture" pour commencer
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {invoices.map((invoice, index) => {
                  const statusInfo = getStatusBadge(invoice.status);
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                    <motion.div
                      key={invoice.id || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                    >
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              Facture - {invoice.order_id}
                            </p>
                            <p className="text-sm text-slate-500">
                              {invoice.customer_name}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-medium text-slate-900">
                              {invoice.total_amount?.toFixed(2)} {invoice.currency}
                            </p>
                            <p className="text-xs text-slate-500">
                              {new Date(invoice.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <Badge className={statusInfo.className}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {invoice.status}
                          </Badge>
                          {invoice.pennylane_id && (
                            <a
                              href={`https://app.pennylane.com/invoices/${invoice.pennylane_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-emerald-600 hover:text-emerald-700"
                              title="Voir sur Pennylane"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          {/* Bouton Envoyer à Back Market */}
                          {invoice.status === 'created' && invoice.pennylane_id && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={sendingBM === invoice.id}
                              className="border-orange-300 text-orange-600 hover:bg-orange-50"
                              onClick={async () => {
                                setSendingBM(invoice.id);
                                try {
                                  const result = await sendInvoiceToBM(invoice.order_id, invoice.id);
                                  toast.success(`Facture envoyée à ${result.email}`);
                                  fetchInvoices();
                                } catch (error) {
                                  const msg = error.response?.data?.detail || 'Erreur envoi';
                                  toast.error(msg);
                                } finally {
                                  setSendingBM(null);
                                }
                              }}
                              data-testid={`send-to-bm-${invoice.order_id}`}
                            >
                              {sendingBM === invoice.id ? (
                                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <Send className="w-3 h-3 mr-1" />
                              )}
                              Envoyer BM
                            </Button>
                          )}
                          {invoice.status === 'sent' && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                              <CheckCircle className="w-3 h-3 mr-1" /> Envoyée BM
                            </Badge>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Information box about Back Market sending */}
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-orange-900">Envoi vers Back Market désactivé</h3>
                <p className="text-sm text-orange-700 mt-1">
                  L'envoi automatique des factures vers Back Market est actuellement désactivé. 
                  Cette fonctionnalité sera activée une fois que vous aurez validé que les factures 
                  Pennylane sont correctes et en mode production (pas en version d'essai).
                </p>
                <p className="text-xs text-orange-600 mt-2">
                  En attendant, vous pouvez télécharger les factures depuis Pennylane et les envoyer manuellement via le Back Office Back Market.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
